import { useRef, useCallback, useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Link,
  Image,
  List,
  ListOrdered,
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

interface ToolbarButtonProps {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  active?: boolean;
}

function ToolbarButton({ icon, title, onClick, active }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
        active
          ? 'bg-brand-50 text-brand-600'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
      }`}
    >
      {icon}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-slate-200 mx-1" />;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Digite aqui...',
  minHeight = '150px',
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val);
    editorRef.current?.focus();
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleBold = useCallback(() => {
    execCommand('bold');
  }, [execCommand]);

  const handleItalic = useCallback(() => {
    execCommand('italic');
  }, [execCommand]);

  const handleUnderline = useCallback(() => {
    execCommand('underline');
  }, [execCommand]);

  const handleLink = useCallback(() => {
    const url = window.prompt('Insira o URL do link:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  const handleImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const url = URL.createObjectURL(file);
        execCommand('insertImage', url);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [execCommand]
  );

  const handleUnorderedList = useCallback(() => {
    execCommand('insertUnorderedList');
  }, [execCommand]);

  const handleOrderedList = useCallback(() => {
    execCommand('insertOrderedList');
  }, [execCommand]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            handleBold();
            break;
          case 'i':
            e.preventDefault();
            handleItalic();
            break;
          case 'u':
            e.preventDefault();
            handleUnderline();
            break;
        }
      }
    },
    [handleBold, handleItalic, handleUnderline]
  );

  return (
    <div className="rounded-xl border border-slate-200 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 transition-all overflow-hidden">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolbarButton
          icon={<Bold size={15} />}
          title="Negrito (Ctrl+B)"
          onClick={handleBold}
        />
        <ToolbarButton
          icon={<Italic size={15} />}
          title="Itálico (Ctrl+I)"
          onClick={handleItalic}
        />
        <ToolbarButton
          icon={<Underline size={15} />}
          title="Sublinhado (Ctrl+U)"
          onClick={handleUnderline}
        />
        <ToolbarDivider />
        <ToolbarButton
          icon={<Link size={15} />}
          title="Inserir link"
          onClick={handleLink}
        />
        <ToolbarButton
          icon={<Image size={15} />}
          title="Inserir imagem"
          onClick={handleImageClick}
        />
        <ToolbarDivider />
        <ToolbarButton
          icon={<List size={15} />}
          title="Lista com marcadores"
          onClick={handleUnorderedList}
        />
        <ToolbarButton
          icon={<ListOrdered size={15} />}
          title="Lista numerada"
          onClick={handleOrderedList}
        />
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
      />
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        style={{ minHeight }}
        className="rich-text-editor-content px-3 py-2 outline-none text-sm text-slate-800 prose prose-sm max-w-none [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2 [&_a]:text-brand-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-0.5 empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
      />
    </div>
  );
}
